import { useEffect, useState } from 'react';
import './schedule-view.styles.scss';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import TeamName from '@/components/SHARED/team-name/team-name.component';

// League schedule by game day. schedule: summary/schedule; teams:
// summary/teams `teams` map. currentDay: the last day played (0 before the
// season), which the view opens on.
const ScheduleView = ({ schedule, teams, currentDay, gameLink }) => {

    const [ day, setDay ] = useState(Math.max(1, currentDay ?? 1));

    useEffect(() => {
        setDay(Math.max(1, currentDay ?? 1));
    }, [currentDay])

    if (!schedule || !teams) return null;

    const games = schedule.games.filter(g => g.day === day);
    const playing = new Set(games.flatMap(g => [g.home, g.away]));
    // In the postseason most teams are done, so only list idle teams in
    // the regular season.
    const postseasonDay = games.some(g => g.post) || (games.length === 0 && day > 1);
    const idle = postseasonDay ? [] : Object.values(teams).filter(t => !playing.has(t.id));

    return (
        <div className='schedule-view'>
            <div className='day-picker'>
                <button className='g-button small-btn' disabled={day <= 1} onClick={() => setDay(day - 1)} aria-label='Previous day'>
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <select value={day} onChange={(e) => setDay(Number(e.target.value))} aria-label='Game day'>
                    {Array.from({ length: schedule.days }, (_, i) => i + 1).map(d => (
                        <option key={d} value={d}>Day {d}{d === currentDay ? ' (latest)' : ''}</option>
                    ))}
                </select>
                <button className='g-button small-btn' disabled={day >= schedule.days} onClick={() => setDay(day + 1)} aria-label='Next day'>
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>

            {games.length === 0 && <p className='meta'>No games this day.</p>}
            <div className='game-list'>
                {games.map(g => {
                    const home = teams[g.home];
                    const away = teams[g.away];
                    const r = g.result;
                    const row = (
                        <>
                            <div className={`game-team ${r && r.away > r.home ? 'won' : ''}`}>
                                <TeamBadge team={away} size='sm' />
                                <span className='game-team-name'><TeamName team={away} /></span>
                                <span className='game-score'>{r ? r.away : ''}</span>
                            </div>
                            <div className={`game-team ${r && r.home > r.away ? 'won' : ''}`}>
                                <TeamBadge team={home} size='sm' />
                                <span className='game-team-name'><TeamName team={home} /></span>
                                <span className='game-score'>{r ? r.home : ''}</span>
                            </div>
                            <div className='game-meta meta'>
                                {r ? `Final${r.ot ? ` (${r.ot === 1 ? 'OT' : `${r.ot}OT`})` : ''}` : 'Scheduled'}
                                {g.conf ? ' · Conference' : ''}
                                {g.label && <div className='game-label'>{g.label}</div>}
                            </div>
                        </>
                    );
                    return r ?
                        <Link key={g.id} to={gameLink(g.id)} className='g-card game-card played'>{row}</Link> :
                        <div key={g.id} className='g-card game-card'>{row}</div>;
                })}
            </div>
            {idle.length > 0 &&
                <p className='meta idle'>
                    No game today: {idle.map(t => t.name).sort().join(', ')}
                </p>
            }
        </div>
    )
}

export default ScheduleView
